/**
 * userSettings.ts — Unified per-user settings persistence
 * BETA 16.56 — PSBOT cycle-31: per Dylan: "all my seting i ever change need
 * to save and be the same anytime i come back to play. that is why we have
 * our own user sign in"
 *
 * Wave 1 of the mega settings rollout. Single source of truth for ALL game
 * settings. Stored in localStorage keyed by username so each player has
 * their own profile on the device. Anonymous users get an "anonymous" key.
 *
 * Future (separate ship): sync these settings up to Supabase on login so
 * settings follow the user across devices. The shape here is designed to
 * be flat-JSON serializable so DB sync is straightforward.
 */

const STORAGE_KEY_PREFIX = 'goopzone_user_settings_';
const ANON_KEY = 'anonymous';

export interface UserSettings {
  // Audio (Wave 1)
  masterVolume: number;       // 0..1
  musicVolume: number;        // 0..1
  sfxVolume: number;          // 0..1

  // Display (Wave 1)
  hudScale: number;           // 0.75..2.0

  // Graphics (Wave 4 — initialized to defaults; readers fall back gracefully)
  particleDensity?: 'low' | 'medium' | 'high' | 'off'; // default 'high'
  // BETA 16.64 — Resolution preset (Dylan: "1080p, 2k, 4k, 720p, and it
  // auto chose"). Stored as the named preset; the numeric scale is
  // derived from preset + viewport.
  resolutionPreset?: 'auto' | '720p' | '1080p' | '1440p' | 'native'; // default 'auto'
  resolutionScale?: number;    // legacy — kept for migration
  screenShakeEnabled?: boolean; // default true
  glowEffectsEnabled?: boolean; // default true
  screenFlashEnabled?: boolean; // default true
  dynamicLightingEnabled?: boolean; // BETA 20.99 — moody dark + player glow. default FALSE (experimental)

  // Controls (Wave 3)
  controllerEnabled?: boolean; // default true (auto-detect on connect)
  touchControlsForced?: boolean; // default false (touch auto-detected)
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  masterVolume: 0.7,
  musicVolume: 0.35,
  sfxVolume: 0.6,
  hudScale: 1.0,
  particleDensity: 'high',
  resolutionPreset: 'auto',
  resolutionScale: 1.0,
  screenShakeEnabled: true,
  glowEffectsEnabled: true,
  screenFlashEnabled: true,
  dynamicLightingEnabled: false,
  controllerEnabled: true,
  touchControlsForced: false,
};

function keyFor(username: string | undefined | null): string {
  const u = (username ?? '').trim();
  return STORAGE_KEY_PREFIX + (u || ANON_KEY);
}

/**
 * Load the saved settings for a username (or anonymous). Falls back to
 * defaults for any field not present.
 *
 * Backwards compat: if there's no per-user record but the legacy
 * `goopzone_audio_settings` blob exists, migrate it once into the
 * anonymous key so existing players don't lose their volume.
 */
export function loadUserSettings(username?: string | null): UserSettings {
  try {
    const raw = localStorage.getItem(keyFor(username));
    if (raw) {
      return { ...DEFAULT_USER_SETTINGS, ...JSON.parse(raw) };
    }
    // Legacy migration — old key was just for audio.
    const legacy = localStorage.getItem('goopzone_audio_settings');
    if (legacy) {
      const parsed = JSON.parse(legacy);
      const migrated: UserSettings = { ...DEFAULT_USER_SETTINGS, ...parsed };
      saveUserSettings(migrated, username);
      return migrated;
    }
  } catch (_e) {
    // Corrupted JSON or localStorage blocked — fall through to defaults
  }
  return { ...DEFAULT_USER_SETTINGS };
}

/** Save settings for the current user. */
export function saveUserSettings(settings: UserSettings, username?: string | null): void {
  try {
    localStorage.setItem(keyFor(username), JSON.stringify(settings));
  } catch (_e) { /* localStorage full or blocked — silent ignore */ }
}

/** Patch a single field and persist. Returns the merged settings object. */
export function updateUserSetting<K extends keyof UserSettings>(
  current: UserSettings,
  username: string | null | undefined,
  key: K,
  value: UserSettings[K],
): UserSettings {
  const next: UserSettings = { ...current, [key]: value };
  saveUserSettings(next, username);
  return next;
}
